import {LitElement, nothing, type PropertyValues} from "lit";
import {customElement, property, state} from "lit/decorators.js";
import {unsafeHTML} from "lit/directives/unsafe-html.js";
import {html, unsafeStatic} from "lit/static-html.js";

import {ROOT_TAG} from "../../globals";
import {InterstateIcon} from "../../interstate";
import {htmlToText, safeHref, sanitizeHtml} from "../../security";
import type {
  SideNavEntry,
  SideNavIcon,
  SideNavIconSize
} from "./sideNavEntries";

export const tagName = `${ROOT_TAG}-side-nav-item`;
export const SideNavItem = unsafeStatic(tagName);

export type SideNavItemLayout = "overlay" | "rail" | "mobile-menu";

const LAYOUT_CLASSES: Record<SideNavItemLayout, string> = {
  "overlay":
    "ids-local-nav-item flex-col items-center justify-center gap-1 w-full text-center text-xs whitespace-normal rounded text-neutral-900 visited:text-neutral-900 hover:text-primary-900 hover:bg-primary-50 dark:text-white dark:visited:text-white dark:hover:text-white dark:hover:!bg-primary-600 [:not(:first-child)>&]:![--ids-comp-local-nav-item-margin-top:inherit]",
  "rail":
    "ids-local-nav-item flex-col items-center justify-center gap-1 w-full text-center text-xs whitespace-normal rounded text-neutral-900 visited:text-neutral-900 hover:text-primary-900 hover:bg-primary-50 dark:!text-white dark:visited:!text-white dark:hover:!bg-primary-600 [:not(:first-child)>&]:![--ids-comp-local-nav-item-margin-top:inherit]",
  "mobile-menu":
    "ids-local-nav-item flex-row items-center justify-start gap-3 whitespace-normal px-4 py-2 text-left rounded text-neutral-900 visited:text-neutral-900 hover:text-primary-900 hover:bg-primary-50 dark:text-white dark:visited:text-white dark:hover:text-white dark:hover:!bg-primary-600 [:not(:first-child)>&]:![--ids-comp-local-nav-item-margin-top:inherit]"
};

const SELECTED_SUFFIX =
  " !bg-primary-50 !text-primary-900 !border-primary-900 dark:!bg-primary-600 dark:!text-white dark:!border-primary-200";

const SELECTED_LAYOUT_CLASSES: Record<SideNavItemLayout, string> = {
  "overlay": LAYOUT_CLASSES.overlay + SELECTED_SUFFIX,
  "rail": LAYOUT_CLASSES.rail + SELECTED_SUFFIX,
  "mobile-menu": LAYOUT_CLASSES["mobile-menu"] + SELECTED_SUFFIX
};

const LABEL_CLASSES: Record<SideNavItemLayout, string> = {
  "overlay": "min-w-0",
  "rail": "",
  "mobile-menu": "min-w-0"
};

const ICON_SIZE_CLASSES: Record<SideNavIconSize, string> = {
  md: "flex-shrink-0 text-2xl leading-none",
  lg: "flex-shrink-0 text-2xl leading-none"
};

/** Per-layout icon size override, taking precedence over ICON_SIZE_CLASSES when set. */
const LAYOUT_ICON_CLASS: Partial<Record<SideNavItemLayout, string>> = {
  "mobile-menu": "flex-shrink-0 text-2xl leading-none"
};

@customElement(tagName)
export class SideNavItemElement extends LitElement {
  @property({attribute: false})
  entry?: SideNavEntry;

  @property()
  layout: SideNavItemLayout = "overlay";

  @property({attribute: false})
  focusable = true;

  @property({type: Boolean, attribute: false})
  selected = false;

  @state()
  private logoFailed = false;

  /** Light DOM, so the side nav's stylesheet and `menu` → `menuitem` ownership reach the row. */
  protected override createRenderRoot() {
    return this;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.setAttribute("role", "none");
  }

  override willUpdate(changed: PropertyValues<this>): void {
    if (changed.has("entry")) {
      this.logoFailed = false;
    }
  }

  private renderIcon(icon: SideNavIcon | null | undefined) {
    if (!icon) {
      return nothing;
    }
    if ("logoUrl" in icon) {
      return this.logoFailed
        ? nothing
        : html`<img
            src=${safeHref(icon.logoUrl)}
            alt=""
            loading="lazy"
            decoding="async"
            referrerpolicy="no-referrer"
            class="h-6 w-6 flex-shrink-0 object-contain rounded-full border border-solid border-neutral-200 dark:border-primary-700"
            @error=${() => (this.logoFailed = true)} />`;
    }
    const iconClass =
      LAYOUT_ICON_CLASS[this.layout] ?? ICON_SIZE_CLASSES[icon.size];
    return html`<${InterstateIcon}
      svg="${icon.svg}"
      class=${iconClass}
    ></${InterstateIcon}>`;
  }

  override render() {
    const entry = this.entry;
    if (!entry) {
      return nothing;
    }
    const isOverlay = this.layout === "overlay";
    const isRail = this.layout === "rail";
    return html`<a
      data-testid=${entry.testId}
      role=${isOverlay ? "menuitem" : nothing}
      href=${safeHref(entry.href)}
      target=${entry.target ?? nothing}
      rel=${entry.target ? "noopener noreferrer" : nothing}
      tabindex=${!isRail && !this.focusable ? "-1" : nothing}
      title=${entry.fullLabel ? htmlToText(entry.fullLabel) : nothing}
      aria-current=${this.selected ? "page" : nothing}
      class=${this.selected
        ? SELECTED_LAYOUT_CLASSES[this.layout]
        : LAYOUT_CLASSES[this.layout]}>
      ${this.renderIcon(entry.icon)}
      <span class=${LABEL_CLASSES[this.layout] || nothing}
        >${unsafeHTML(sanitizeHtml(entry.label))}</span
      >
    </a>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [tagName]: SideNavItemElement;
  }
}
